import type { LegacyRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { PAGE_POSITIONS } from 'types';
import type { AffiliateStockListing, CapiProductPage } from 'types/capi/pdp';
import { fromProductDetailPage } from 'types/TrackableProduct';

import DiscountCode from '~/components/DiscountCode';
import ProductNotificationButton from '~/components/ProductNotificationButton';
import {
  type StockStatus as StockStatusType,
  textColours
} from '~/domain/stockStatus';
import usePageData from '~/hooks/usePageData';
import useRegion from '~/hooks/useRegion';
import AffiliateLogo from '~/routes/pdps/components/WhereToBuy/AffiliateLogo';
import { formatReleaseDateLong } from '~/utils/dates';
import { getStockStatusForSize } from '~/utils/listings';

import { BuyNow, VisitSite } from '../../components/Buttons';
import AffiliateSizes from './AffiliateSizes';
import ShippingAndPrice from './ShippingAndPrice';

type Props = {
  listing: AffiliateStockListing;
  pagePosition: PAGE_POSITIONS;
  position: number;
  selectedSize?: string;
};

export default function StockListing({
  listing,
  position,
  selectedSize,
  pagePosition
}: Props) {
  const product = usePageData<CapiProductPage>();

  const stockStatus = getStockStatusForSize(listing, selectedSize);

  return (
    <div className="flex flex-col gap-4 rounded-lg bg-white p-4">
      <div className="flex gap-4 bg-white">
        <div className="flex grow items-center gap-4">
          <div className="h-9 w-16">
            <AffiliateLogo
              src={listing.affiliate.logo}
              alt={listing.affiliate.name}
            />
          </div>
          <div>
            <StockStatus
              stockStatus={stockStatus}
              listing={listing}
              selectedSize={selectedSize}
            />
          </div>
        </div>
        <ShippingAndPrice listing={listing} selectedSize={selectedSize} />
      </div>
      <AffiliateSizes
        product={fromProductDetailPage(product)}
        price={listing.price}
        affiliate={listing.affiliate}
        selectedSize={selectedSize}
        sizes={listing.sizes}
        urlTemplates={listing}
        position={position}
        pagePosition={pagePosition}
      />
      <div className="flex w-full flex-col gap-2">
        <DiscountCode
          code={listing.discount_code?.code}
          amount={listing.discount_code?.amount}
        />
        <StockListingButtons
          stockStatus={stockStatus}
          selectedSize={selectedSize}
          listing={listing}
          position={position}
          pagePosition={pagePosition}
        />
      </div>
    </div>
  );
}

export const StockStatus = ({
  stockStatus,
  listing,
  selectedSize
}: {
  listing: AffiliateStockListing;
  selectedSize?: string;
  stockStatus: StockStatusType;
}) => {
  const { t } = useTranslation();
  const region = useRegion();

  switch (stockStatus) {
    case 'in-stock':
      return (
        <>
          {listing.affiliate.is_reseller ? (
            <>
              <p className={`text-sm ${textColours['reseller']} md:text-base`}>
                Marketplace
              </p>
              {listing.affiliate.id === 'affiliate:marketplace' && (
                <p className="text-xs text-black md:text-sm">
                  Verified sellers
                </p>
              )}
            </>
          ) : (
            <p className={`text-sm ${textColours[stockStatus]} md:text-base`}>
              {t(stockStatus)}
            </p>
          )}
        </>
      );
    case 'coming-soon':
      return (
        <div className="flex flex-col text-sm md:text-base">
          <p className={`${textColours[stockStatus]}`}>{t(stockStatus)}</p>
          <span>
            {listing.release_date
              ? formatReleaseDateLong(listing.release_date, region)
              : t('tbcLabel')}
          </span>
        </div>
      );
    case 'sold-out':
      return (
        <div className={`text-sm ${textColours[stockStatus]} md:text-base`}>
          {listing.sizes.length && selectedSize ? (
            <>
              <p>{t(stockStatus)}</p>
              <p className="text-black">UK {selectedSize}</p>
            </>
          ) : (
            t(stockStatus)
          )}
        </div>
      );
    case 'tbc':
    default:
      return <p className={textColours[stockStatus]}>{t('tbc')}</p>;
  }
};

type StockListingButtonsProps = {
  buttonsRef?: LegacyRef<HTMLDivElement>;
  dynamicVariant?: boolean;
  listing: AffiliateStockListing;
  onBuyNowClick?: () => void;
  pagePosition: PAGE_POSITIONS;
  position: number;
  selectedSize?: string;
  showAfffiliateLogo?: boolean;
  stockStatus: StockStatusType;
};

export function StockListingButtons({
  stockStatus,
  pagePosition,
  selectedSize,
  listing,
  position,
  onBuyNowClick,
  showAfffiliateLogo,
  dynamicVariant
}: StockListingButtonsProps) {
  const product = usePageData<CapiProductPage>();
  const region = useRegion();

  const areNotificationsAllowed =
    product.allow_user_notifications && region.code === 'GB';

  return (
    <div data-cy="stock-listing-buttons">
      {stockStatus === 'in-stock' ? (
        <BuyNow
          onClick={onBuyNowClick}
          showAfffiliateLogo={showAfffiliateLogo}
          selectedSize={selectedSize}
          stockListing={listing}
          buttonPosition={position}
          pagePosition={pagePosition}
          variant={
            !dynamicVariant
              ? 'forest'
              : selectedSize
                ? 'green-cta'
                : 'black-cta'
          }
        />
      ) : (
        <div
          className={
            product.allow_user_notifications && areNotificationsAllowed
              ? 'flex gap-2'
              : ''
          }
        >
          <VisitSite
            stockListing={listing}
            buttonPosition={position}
            pagePosition={pagePosition}
            className="min-w-0 flex-1 text-nowrap"
          />
          {product.allow_user_notifications && areNotificationsAllowed && (
            <ProductNotificationButton
              product={{
                affiliateName: listing.affiliate.name,
                id: product.product_id,
                image: product.gallery[0],
                legacyId: product.legacy_id,
                name: product.name,
                price: product.price.regular
              }}
              className="h-12 flex-1"
              pagePosition="WTB"
              buttonStyle="normal"
            />
          )}
        </div>
      )}
    </div>
  );
}
